import dayjs from "dayjs";
import type { AssignmentSummary, AssignmentSummaryType } from "../components/scheduling/RecentAssignments";
import { cleaningLabel } from "../components/scheduling/other/common";
import { ISODateString } from "../types/date";
import { AVAttendantAsset, AVAttendantAssignmentType } from "../types/scheduling/avAttendant";
import { MidweekMeetingPartAsset, MidweekMeetingPartType } from "../types/scheduling/midweek";
import { OtherSchedule, OtherScheduleKind } from "../types/scheduling/other";
import { CongSettings } from "../types/scheduling/settings";
import { WMCoreParts, WMCorePartTitleAsset, WMSchedule } from "../types/scheduling/weekend";

import { weekOfString } from "./dateHelpers";
import { t } from "./locale";

export const isMidweekPart = (partType?: AssignmentSummaryType): boolean => {
  if (!partType) return false;
  return Object.values(MidweekMeetingPartType).includes(partType as MidweekMeetingPartType);
};

export const isWeekendPart = (partType?: AssignmentSummaryType): boolean => {
  if (!partType) return false;
  return (
    Object.values(WMCoreParts).includes(partType as WMCoreParts) || ["publicTalk", "publicTalkOut"].includes(partType)
  );
};

export const isAVPart = (partType?: AssignmentSummaryType): boolean => {
  if (!partType) return false;
  return Object.values(AVAttendantAssignmentType).includes(partType as AVAttendantAssignmentType);
};

const getSundayOfWeek = (date: ISODateString): ISODateString => {
  return weekOfString(date, 7);
};

export const getAssignmentLabel = (assignment: AssignmentSummary): string => {
  if (assignment.label) {
    return assignment.label;
  }

  let label: string;

  if (isAVPart(assignment.type)) {
    label = t(AVAttendantAsset[assignment.type as AVAttendantAssignmentType]);
  } else if (isMidweekPart(assignment.type)) {
    label = t(MidweekMeetingPartAsset[assignment.type as MidweekMeetingPartType]);
  } else if (isWeekendPart(assignment.type)) {
    label = ["publicTalk", "publicTalkOut"].includes(assignment.type)
      ? t("schedules.weekend.speaker.0")
      : t(WMCorePartTitleAsset[assignment.type as WMCoreParts]);
  } else if (assignment.type === OtherScheduleKind.FieldMinistry) {
    label = t("schedules.field-service.title");
  } else {
    return t("schedules.potential-conflict");
  }

  return label;
};

export const getOverlappingAssignmentLabel = ({
  userId,
  assignDate,
  partType,
  assignmentMap,
  congSettings,
}: {
  userId: number;
  assignDate: ISODateString;
  partType: AssignmentSummaryType | undefined;
  assignmentMap: Map<number, AssignmentSummary[]>;
  congSettings: CongSettings;
}): string | null => {
  if (!assignmentMap || !congSettings) return null;

  const isPartMidweekPart = isMidweekPart(partType);
  const isPartWeekendPart = isWeekendPart(partType);
  const isPartAVPart = isAVPart(partType);
  const weekOf = weekOfString(assignDate);
  const isPartOnWeekend = [6, 0].includes(dayjs(assignDate).day());

  const userAssignments = assignmentMap.get(userId) ?? [];

  // if this person is the designated WTC and no one else is assigned for this week, treat it as
  // an assignment for conflict-checking purposes (without mutating the shared assignmentMap, since
  // callers may render that map directly)
  const existingWTC =
    congSettings.wt_conductor === userId &&
    [...assignmentMap.values()].flat().find((as) => as.date === weekOf && as.type === WMCoreParts.WTConductor);
  const candidateAssignments =
    congSettings.wt_conductor === userId && !existingWTC
      ? [...userAssignments, { date: weekOf, type: WMCoreParts.WTConductor, userId: userId }]
      : userAssignments;

  const conflictingAssignment = candidateAssignments.find((assignment) => {
    const isAssignmentMidweekPart = isMidweekPart(assignment.type);
    const isAssignmentWeekendPart = isWeekendPart(assignment.type);
    const isAssignmentAVPart = isAVPart(assignment.type);

    // if the assignment is hospitality, don't let it conflict with anything except a speaking assignment
    if (assignment.type === WMCoreParts.Host) {
      return partType === "publicTalk" || partType === "publicTalkOut";
    }

    // the reverse of the case above: being the designated WT conductor doesn't conflict with hospitality either
    if (assignment.type === WMCoreParts.WTConductor && partType === WMCoreParts.Host) {
      return false;
    }

    // skip if it's their own existing assignment for this same part this same week. Comparing by
    // week (rather than day-of-week, which was the previous check) matters because WM/MM assignment
    // dates are stored as the canonical Monday of the week, not the actual meeting date, so a real
    // weekend/midweek assignDate never shares a weekday with its own assignment.date
    if (assignment.type === partType && weekOfString(assignment.date) === weekOf) return false;

    if ((isPartMidweekPart && isAssignmentMidweekPart) || (isPartWeekendPart && isAssignmentWeekendPart)) {
      return assignment.date === weekOf;
    }

    if (isAssignmentAVPart && (congSettings.ava_schedule_weekly || isPartMidweekPart)) {
      return assignment.date === weekOf;
    }
    if (isAssignmentAVPart && !congSettings.ava_schedule_weekly && isPartWeekendPart) {
      return assignment.date === getSundayOfWeek(assignDate);
    }
    if (isPartAVPart && congSettings.ava_schedule_weekly && (isAssignmentMidweekPart || isAssignmentWeekendPart)) {
      return weekOfString(assignment.date) == weekOf;
    }

    if (isPartAVPart && !congSettings.ava_schedule_weekly) {
      if (isPartOnWeekend && isAssignmentWeekendPart) return weekOf === assignment.date;
      if (!isPartOnWeekend && isAssignmentMidweekPart) return assignment.date === weekOf;
      if (isPartOnWeekend && isAssignmentAVPart) return assignment.date === getSundayOfWeek(assignDate);
      if (!isPartOnWeekend && isAssignmentAVPart) return assignment.date === weekOf;
      return false;
    }

    // exact date match for the rest of other assignments
    return assignment.date === assignDate;
  });

  return conflictingAssignment ? getAssignmentLabel(conflictingAssignment) : null;
};

const sameWeek = (a: ISODateString, b: ISODateString): boolean => weekOfString(a) === weekOfString(b);

// Weekend hospitality and hall cleaning being assigned to the same field service group the same
// week is a potential conflict. This only applies to group-based hospitality (congregation setting
// hospitality_by_group), since that's the only case where a host assignment and a cleaning
// assignment are keyed by the same value (the group's overseer's user id) - see
// getCleaningHospitalityConflictLabel below for the reverse check, used on the cleaning schedule.

export const getHospitalityCleaningConflictLabel = ({
  hostUserId,
  weekendDate,
  cleaningSchedules,
  congSettings,
}: {
  hostUserId: number;
  weekendDate: ISODateString;
  cleaningSchedules: OtherSchedule[];
  congSettings: CongSettings;
}): string | null => {
  if (!hostUserId) return null;

  const conflictingSchedule = cleaningSchedules.find(
    (os) => !os.individual && os.assigneeUserId === hostUserId && sameWeek(os.date, weekendDate),
  );
  if (!conflictingSchedule) return null;

  // include which cleaning slot conflicts (e.g. "Kingdom Hall - Group 1") so it's clear at a
  // glance what the conflicting assignment is, since a congregation can have several
  const slotLabel = cleaningLabel(congSettings, conflictingSchedule.slot);
  return slotLabel ? `${t("schedules.cleaning.title")}: ${slotLabel}` : t("schedules.cleaning.title");
};

export const getCleaningHospitalityConflictLabel = ({
  groupOverseerId,
  cleaningDate,
  wmSchedules,
}: {
  groupOverseerId: number;
  cleaningDate: ISODateString;
  wmSchedules: WMSchedule[];
}): string | null => {
  if (!groupOverseerId) return null;

  const hasHostConflict = wmSchedules.some((wm) => wm.host === groupOverseerId && sameWeek(wm.date, cleaningDate));

  return hasHostConflict ? t("schedules.privileges.hospitality") : null;
};
